import { getFileExtension, getFileType } from 'utils/filesUtils';
import { contactType } from 'common/dropdownOptions';
import { encode } from 'utils/encryptionUtils';

/**
 * Get file type from file name
 */
function findType(item) {
  return getFileType(getFileExtension(item?.file_name));
}

/**
 * Generate navigation path for search result item
 * @param {Object} data - Search result item
 * @param {boolean} showContent - Whether content search is active
 * @returns {string|Object} - Navigation path or route object
 */
export function goToDetails(data, showContent = false) {
  if (data?.res_type === 'case' || data?.res_type === 'lead') {
    if (data?.is_lead || data?.res_type === 'lead') {
      return `/leads/${data?.case_id}`;
    } else {
      return `/cases/${data?.case_id}`;
    }
  } else if (data?.res_type === 'message') {
    return `/messages/${data?.message_thread_id}`;
  } else if (data?.res_type === 'message-reply') {
    return `/messages/${data?.pk?.split('#')?.[1]}`;
  } else if (data?.res_type === 'email_thread') {
    const threadParams = new URLSearchParams();
    if (data?.pk) threadParams.set('thread_pk', encode(data.pk));
    if (data?.sk) threadParams.set('thread_sk', encode(data.sk));
    return {
      pathname: '/mails-v2',
      search: `?${threadParams.toString()}`,
      state: {
        emailThread: {
          pk: data?.pk,
          sk: data?.sk,
        },
      },
    };
  } else if (data?.res_type === 'text_message_thread' || data?.res_type === 'text_message_reply') {
    // Get contact_id only from contact_id field
    const contactId = data?.contact_id;

    // Try to get phone number from multiple sources
    let phoneNumber = data?.to || data?.To || data?.subtitle;

    // If phone number is a number, convert to string
    if (typeof phoneNumber === 'number') {
      phoneNumber = String(phoneNumber);
    }

    // If still no phone number, try to extract from pk (format: tenant_id#phone_number)
    if (!phoneNumber && data?.pk) {
      const pkParts = data.pk.split('#');
      if (pkParts.length > 1) {
        phoneNumber = pkParts[pkParts.length - 1];
      }
    }

    // Ensure phone number has + prefix if it's a valid number
    if (phoneNumber && !phoneNumber.startsWith('+')) {
      // Remove all non-digits, then add + prefix
      const digitsOnly = phoneNumber.replace(/\D/g, '');
      if (digitsOnly) {
        phoneNumber = '+' + digitsOnly;
      }
    }

    // If we have both contact_id and phone number, navigate to the specific thread
    if (contactId && phoneNumber) {
      return `/text-message/${contactId}/${phoneNumber}?tab=0`;
    } else if (contactId && !phoneNumber) {
      // If we have contact_id but no phone number, navigate to text messages list
      // The contact_id might help filter or the page can handle it
      return '/text-messages';
    } else if (phoneNumber && !contactId) {
      // If we only have phone number but no contact_id, navigate to text messages list
      // Note: pk format is tenant_id#phone_number, so first part is tenant_id, not contact_id
      // contact_id should be provided by API for proper navigation
      return '/text-messages';
    } else {
      // If neither contact_id nor phone number is available, navigate to text messages list
      return '/text-messages';
    }
  } else if (data?.res_type === 'file_system_entry' || data?.res_type?.includes('file_system_entry') || showContent) {
    let fileId = data?.object_id || data?.file_id || data?.document_id;
    if (!fileId && data?.sk) {
      const skParts = data.sk.split('#');
      fileId = skParts.length > 1 ? skParts[skParts.length - 1] : null;
    }

    const isFolder = data?.entry_type === 'folder' || (!data?.entry_type && data?.sk?.startsWith('file_system_entry#'));

    if (isFolder) {
      const folderLabel = data?.display_name || data?.folderName || data?.title || '';
      // Encode breadCrumb into URL so new-tab navigation works without location.state
      const breadCrumbEncoded = encode(JSON.stringify([{ label: folderLabel, value: fileId }]));
      const state = {
        breadCrumb: { label: folderLabel, value: fileId },
        res_type_id: data?.res_type_id || fileId,
      };

      if (data?.case_id && data?.case_id !== 'undefined') {
        return {
          pathname: `/${data?.is_lead ? 'leads' : 'cases'}/${data?.case_id}/files`,
          search: `?breadCrumb=${breadCrumbEncoded}`,
          state,
        };
      } else {
        return {
          pathname: `/file-manager/firm-documents`,
          search: `?breadCrumb=${breadCrumbEncoded}`,
          state,
        };
      }
    }

    localStorage.setItem('viewFile', JSON.stringify(data));

    const fileData = { ...data, object_id: fileId };
    const folderId = data?.folder_id || data?.folderId;
    const folderName = data?.folder_name || data?.folderName;
    const state = {
      file: fileData,
      res_type_id: data?.res_type_id || fileId,
    };

    let breadCrumbData = null;
    if (data?.folder_hierarchy?.length > 0) {
      breadCrumbData = data.folder_hierarchy.map((f) => ({ label: f.folder_name, value: f.folder_id }));
    } else if (data?.breadcrumb_path?.length > 0) {
      const folderCrumbs = data.breadcrumb_path.filter((b) => b.type === 'folder');
      if (folderCrumbs.length > 0) {
        breadCrumbData = folderCrumbs.map((b) => ({ label: b.name, value: b.id }));
      }
    }
    if (!breadCrumbData && folderId) {
      breadCrumbData = [{ label: folderName || '', value: folderId }];
    }

    const urlParams = new URLSearchParams();
    if (fileId) urlParams.set('file_id', fileId);
    if (breadCrumbData?.length > 0) {
      urlParams.set('breadCrumb', encode(JSON.stringify(breadCrumbData)));
      const lastCrumb = breadCrumbData[breadCrumbData.length - 1];
      state.breadCrumb = { label: lastCrumb.label, value: lastCrumb.value };
      state.breadCrumbList = breadCrumbData;
    }
    const searchParam = `?${urlParams.toString()}`;

    if (data?.case_id && data?.case_id !== 'undefined') {
      return {
        pathname: `/${data?.is_lead ? 'leads' : 'cases'}/${data?.case_id}/files`,
        search: searchParam,
        state,
      };
    } else {
      return {
        pathname: `/file-manager/firm-documents`,
        search: searchParam,
        state,
      };
    }
  } else if (data?.res_type === 'event') {
    return {
      pathname: '/calendar',
      search: `?id=${encodeURIComponent(data?.sk)}`,
    };
  } else if (data?.res_type === 'user') {
    if (data?.access_level && data?.access_level !== 'client') {
      return `/settings/team-management/${data?.user_id}`;
    } else if (contactType.includes(data?.contact_type || !data?.access_level || data?.access_level === 'client')) {
      return `/contacts/${data?.contact_id}`;
    }
  } else if (data?.res_type === 'task') {
    let taskId = data?.task_id;
    if (!taskId && data?.sk) {
      const skParts = data.sk.split('#');
      taskId = skParts.length > 1 ? skParts[skParts.length - 1] : null;
    }

    const searchParam = taskId ? `?id=${taskId}` : '';
    return {
      pathname: '/tasks',
      search: searchParam,
      state: { viewTask: data },
    };
  } else if (data?.res_type === 'invoice') {
    let invoiceId = null;
    if (data?.sk && data.sk.includes('#')) {
      const skParts = data.sk.split('#');
      invoiceId = skParts[skParts.length - 1];
    }

    const invoiceSk = invoiceId ? `invoice#${invoiceId}` : null;
    const searchParam = invoiceSk ? `?invoice_sk=${encodeURIComponent(invoiceSk)}` : '';

    return {
      pathname: `/cases/${data?.case_id ? data?.case_id : data?.invoice?.case_id}/billing`,
      search: searchParam,
    };
  } else if (data?.res_type === 'payment_request') {
    let paymentRequestId = null;
    if (data?.sk && data.sk.includes('#')) {
      const skParts = data.sk.split('#');
      paymentRequestId = skParts[skParts.length - 1];
    }

    const paymentRequestSk = paymentRequestId ? `payment_request#${paymentRequestId}` : null;
    const searchParam = paymentRequestSk ? `?payment_request_sk=${encodeURIComponent(paymentRequestSk)}` : '';

    return {
      pathname: `/cases/${data?.case_id}/billing`,
      search: searchParam,
    };
  } else if (data?.res_type === 'case-note') {
    let caseNoteId = data?.case_note_id;
    if (!caseNoteId && data?.sk) {
      const skParts = data.sk.split('#');
      caseNoteId = skParts.length > 1 ? skParts[skParts.length - 1] : null;
    }

    const pathname = caseNoteId ? `/cases/${data?.case_id}/notes/${caseNoteId}` : `/cases/${data?.case_id}/notes`;

    return {
      pathname,
      state: { case_note: data },
    };
  }
  return '/';
}
